<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <acc-params-details v-if="items" class="pa-2" :items="items" />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="2">
        <v-menu
          ref="menuPeriod"
          v-model="menuPeriod"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="form.period | periodFormat"
              :label="`${$t('EREPORTER.PERIOD')} *`"
              append-icon="mdi-calendar"
              clearable
              readonly
              :rules="rules.requiredTextLine"
              role="button"
              autocomplete="nope"
              @click:clear="clearPeriod"
              v-on="on"
            />
          </template>
          <v-date-picker
            ref="picker"
            v-model="form.period"
            type="month"
            min="1940-01-01"
            @change="savePeriod"
          />
        </v-menu>
      </v-col>
      <v-col cols="4">
        <v-select
          v-if="disabledBuildingComplex"
          v-model="buildingComplex.id"
          item-text="name"
          item-value="id"
          :items="buildingComplexesList"
          disabled
          :label="`${$t('BUILDING_COMPLEXES.ITEM')}`"
        ></v-select>
        <v-select
          v-else
          v-model="form.building_complex_id"
          item-text="name"
          item-value="id"
          :items="buildingComplexesList"
          :label="`${$t('BUILDING_COMPLEXES.ITEM')}`"
        ></v-select>
      </v-col>
      <v-col cols="3">
        <v-select
          v-model="form.account_id"
          :items="accountParams"
          :label="`${$t('EREPORTER.ACC_PARAMS')} *`"
          :rules="rules.requiredSelectNotArray"
          item-value="id"
          item-text="name"
        />
      </v-col>
      <v-col cols="3">
        <v-select
          v-model="form.addressable_id"
          :items="addresses"
          :label="`${$t('EREPORTER.INVOICES.BILLING_ADDRESS')} *`"
          :rules="rules.requiredSelectNotArray"
          item-value="addressable_id"
          item-text="address"
        />
      </v-col>
    </v-row>
    <v-row v-for="(el, key) in form.lines" :key="key" dense>
      <v-col cols="11">
        <invoice-line-form
          v-model="form.lines[key]"
          :errors="(errors.lines || [])[key]"
          @input="emitter"
          @update-prop="updatePropWrapPrefix(...arguments, `lines.[${key}]`)"
        ></invoice-line-form>
      </v-col>
      <v-col class="d-flex justify-center align-center">
        <v-btn :disabled="!validLines" icon @click="remove(key)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="3">
        <v-btn color="indigo" dark @click="createLine">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('ITEM.NEW') }} {{ $t('EREPORTER.INVOICES.LINE') }}
        </v-btn>
      </v-col>
      <v-col cols="6"></v-col>
      <v-col cols="3">
        {{ $t('EREPORTER.TOTAL') }}: {{ totalPrice | priceFormat }} CHF
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import InvoiceLineForm from '@/modules/ereporter/components/forms/Invoices/InvoiceManualLineForm';
import {
  requiredTextLine,
  requiredSelectNotArray
} from '@/modules/_global/variables/rules';
import { periodFormat, priceFormat } from '@/modules/_global/filters';
import { isEqual } from 'lodash';
import moment from 'moment';
import formEditable from '@/modules/_global/mixins/form-editable';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import AccParamsDetails from '@/modules/ereporter/components/Invoices/AccParamsDetails';
import customerTypes from '@/modules/ereporter/common/Invoices/customerTypes';

export default {
  name: 'InvoiceManualForm',

  filters: {
    periodFormat,
    priceFormat
  },

  components: { AccParamsDetails, InvoiceLineForm },

  mixins: [formEditable, i18nRefreshData],

  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    buildingComplex: {
      type: Object,
      default: () => ({ id: null })
    },
    disabledBuildingComplex: {
      type: Boolean,
      default: false
    },
    customerId: {
      type: Number,
      required: true
    },
    customerType: {
      type: Number,
      required: true,
      validator: prop => Object.values(customerTypes).includes(prop)
    }
  },
  data: () => ({
    form: {
      period: null,
      lines: [{}]
    },
    menuPeriod: false,
    rules: {
      requiredTextLine,
      requiredSelectNotArray
    },
    accounts: [],
    addresses: []
  }),

  computed: {
    ...mapState('building_complex', ['buildingComplexesList']),
    ...mapState('ereporter', ['accountParams']),

    items() {
      return (
        this.accountParams.find(el => el.id === this.form.account_id) || {}
      ).items;
    },

    validLines() {
      return this.form.lines.length > 1;
    },

    totalPrice() {
      const totalPrice = this.form.lines.reduce(function(total, item) {
        return total + item.unit_price * item.duration;
      }, 0);

      return Math.round((totalPrice + Number.EPSILON) * 100) / 100;
    }
  },

  created() {
    if (!isEqual(this.value, this.form)) {
      this.form.period = moment(Date.now()).format('YYYY-MM');
    }
    this.getDataBuildingComplexes();
    this.refreshData();
  },

  methods: {
    ...mapCacheActions('building_complex', ['getBuildingComplexesList']),
    ...mapCacheActions('ereporter', ['getAccountParams']),
    ...mapActions('ereporter', ['getCustomerAddresses']),

    refreshData() {
      this.getDataAccountParams();
      this.getDataCustomerAddress();
    },

    savePeriod(date) {
      this.$refs.menuPeriod.save(date);
    },
    getDataBuildingComplexes() {
      return this.getBuildingComplexesList({
        params: { statuses: ['active'] }
      }).catch(error => {
        this.error = error;
      });
    },
    getDataAccountParams() {
      return this.getAccountParams().catch(error => {
        this.error = error;
      });
    },
    getDataCustomerAddress() {
      return this.getCustomerAddresses({
        params: {
          customer_id: this.customerId,
          customer_type: this.customerType
        }
      })
        .then(({ data }) => {
          this.addresses = data.data;
          this.setDefaultAddress(data.data);
        })
        .catch(error => {
          this.error = error;
        });
    },
    clearPeriod() {
      this.form.period = null;
    },

    createLine() {
      this.form.lines.push({});
    },

    remove(idx) {
      if (this.validLines) {
        this.form.lines.splice(idx, 1);
      }
    },

    setDefaultAddress(addresses) {
      this.form.addressable_id = addresses.find(
        address => address.type_id === 'main'
      ).addressable_id;
    }
  }
};
</script>

<style lang="scss"></style>
